import { useEffect, useState, useRef } from "react";
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";
import { toast } from "react-toastify";
import "../styles/UserAgreementSign.css";

export default function UserAgreementSign({ agreementId }) {
  const { token } = useAuth();

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState(null);

  /* ================= LOAD AGREEMENT ================= */
  useEffect(() => {
    if (!agreementId) {
      toast.error("Agreement ID missing");
      setLoading(false);
      return;
    }
    fetchAgreement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreementId]);

  const fetchAgreement = async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/agreements/user/${agreementId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await res.json();

      if (res.ok) setAgreement(data.agreement);
      else toast.error(data.message || "Failed to load agreement");
    } catch {
      toast.error("Server error loading agreement");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DRAW SIGN ================= */
  const startDrawing = (e) => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setUploadedSignature(null);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Upload a valid image");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setUploadedSignature(reader.result);
    reader.readAsDataURL(file);
  };

  /* ================= SUBMIT SIGN ================= */
  const submitSignature = async () => {
    let signatureBase64 = uploadedSignature;

    if (!signatureBase64) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      if (!dataUrl || dataUrl.length < 100) {
        toast.error("Draw or upload your signature first");
        return;
      }
      signatureBase64 = dataUrl;
    }

    try {
      setSigning(true);

      const res = await fetch(
        `${API_BASE_URL}/api/agreements/user/sign`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ agreementId, signatureBase64 })
        }
      );

      const data = await res.json();

      if (res.ok) {
        toast.success("Agreement signed successfully");
        fetchAgreement();
        // 🔁 Reload page after 3 seconds
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        toast.error(data.message || "Signing failed");
      }
    } catch {
      toast.error("Server error signing agreement");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return <p>Loading agreement...</p>;
  if (!agreement) return <p>Agreement not found</p>;

  return (
    <div className="uas-container">
      <h2>LEGAL AGREEMENT</h2>

      <div className="uas-preview">
        <h4>{agreement.agreementTitle}</h4>

        {/* ================= USER DETAILS ================= */}
        <div className="uas-details-block">
          <p>
            <span className="uas-label">Name:</span> {agreement.userDetails.name}
          </p>

          <p>
            <span className="uas-label">Email:</span> {agreement.userDetails.email}
          </p>

          <p>
            <span className="uas-label">Mobile:</span>{" "}
            {agreement.userDetails.countryCode
              ? `${agreement.userDetails.countryCode} ${agreement.userDetails.mobile}`
              : agreement.userDetails.mobile || "N/A"}
          </p>

          <p>
            <span className="uas-label">Address:</span>{" "}
            {agreement.userDetails.address || "N/A"}
          </p>
        </div>

        {/* ================= CASE DETAILS ================= */}
        <div className="uas-details-block uas-case-details">
          <h4>Case Details</h4>

          <p>
            <span className="uas-label">Practice Area:</span>{" "}
            {agreement.practiceArea}
          </p>

          <p>
            <span className="uas-label">Case Type:</span>{" "}
            {agreement.caseType}
          </p>

          <p>
            <span className="uas-label">Case Description:</span>{" "}
            {agreement.caseDescription}
          </p>
        </div>

        {/* ================= AGREEMENT BODY ================= */}
        <h4>Statement</h4>
        <p style={{ whiteSpace: "pre-line" }}>
          {agreement.agreementBody}
        </p>

        <h4>Terms & Conditions</h4>
        <p style={{ whiteSpace: "pre-line" }}>
          {agreement.termsAndConditions}
        </p>

        <hr className="uas-divider" />

        {/* ================= SIGNATURE SECTION ================= */}
        <div className="uas-signature-section">

          {/* USER SIGNATURE */}
          <div className="uas-signature-box">
            <p className="uas-section-title">Your Signature</p>

            {agreement.userSignature?.image ? (
              <>
                <img
                  src={agreement.userSignature.image}
                  alt="User Signature"
                  className="uas-signature-image"
                />
                <p className="uas-signed-date">
                  Signed on:{" "}
                  {new Date(agreement.userSignature.signedAt).toLocaleString()}
                </p>
              </>
            ) : agreement.status === "SIGNED_BY_SOLICITOR" && (
              <>
                <canvas
                  ref={canvasRef}
                  className="uas-signature-canvas"
                  width={300}
                  height={120}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="uas-file-input"
                />

                {uploadedSignature && (
                  <img
                    src={uploadedSignature}
                    alt="Signature Preview"
                    className="uas-preview-image"
                  />
                )}

                <div className="uas-btn-row">
                  <button
                    className="uas-clear-btn"
                    onClick={clearSignature}
                  >
                    Clear
                  </button>
                  <button
                    className="uas-primary-btn"
                    onClick={submitSignature}
                    disabled={signing}
                  >
                    {signing ? "Signing..." : "Sign Agreement"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* SOLICITOR SIGNATURE */}
          <div className="uas-signature-box uas-solicitor-box">
            <div className="uas-solicitor-details">
              <p className="uas-section-title">Solicitor Details</p>
              <p>
                <span className="uas-label">Name:</span>{" "}
                {agreement.solicitorDetails.name}
              </p>
              <p>
                <span className="uas-label">Email:</span>{" "}
                {agreement.solicitorDetails.email}
              </p>
            </div>

            <div className="uas-solicitor-signature">
              <p className="uas-section-title">Solicitor Signature</p>

              {agreement.solicitorSignature?.image ? (
                <>
                  <img
                    src={agreement.solicitorSignature.image}
                    alt="Solicitor Signature"
                    className="uas-signature-image"
                  />
                  <p className="uas-signed-date">
                    Signed on:{" "}
                    {new Date(
                      agreement.solicitorSignature.signedAt
                    ).toLocaleString()}
                  </p>
                </>
              ) : (
                <p>Not signed yet</p>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}